import React, { useEffect, useState } from 'react';
import {
  RubricAnalysis,
  RubricCategoryResult,
  RubricEvidence,
  RubricFix,
  RubricStrength
} from '../../types/superadmin';
import { EmptyState } from '../primitives';

/**
 * The Lushin Discovery Call Rubric result, as the super admin sees it.
 *
 * The same `callInsights.rubricAnalysis` the tenant's admin portal renders, but
 * built on THIS portal's tokens (`styles/dashboard.css`) instead of the
 * admin-portal's Tailwind classes — and read-only. Re-analysis and weight
 * tuning stay with the tenant admin who owns the call.
 *
 * Keep the dimension modal's structure in step with
 * `admin-portal/src/components/callInsights/RubricSection.tsx`: a cross-tenant
 * reviewer and the tenant's own admin discussing the same call should be
 * looking at the same thing. Both render bullets where the analyzer produced
 * them and fall back to the paragraph fields where it didn't.
 */

interface Props {
  rubricAnalysis?: RubricAnalysis;
  /** Team-selling call: the score is one combined composite, not two. */
  multipleSalespeople?: boolean;
  /** Jumps the transcript tab to an evidence timestamp. */
  onNavigateToTimestamp?: (iso: string) => void;
}

/** Bands map onto the portal's semantic tones rather than their own colours. */
const BAND_TONE: Record<string, string> = {
  strong: 'pos',
  standard: 'pos',
  soft: 'warn',
  at_risk: 'warn',
  lost: 'neg'
};

/** 1-5 raw score -> tone. Matches `lib/format.ts` thresholds on the 0-100 scale. */
const scoreTone = (score?: number | null): string => {
  if (score == null) return '';
  if (score >= 4) return 'pos';
  if (score >= 3) return 'warn';
  return 'neg';
};

const pad2 = (n?: number): string => String(n ?? 0).padStart(2, '0');

/** One timestamped evidence quote; clicking the stamp jumps to the transcript. */
function EvidenceItem({
  evidence,
  onNavigateToTimestamp
}: {
  evidence: RubricEvidence;
  onNavigateToTimestamp?: (iso: string) => void;
}) {
  const iso = evidence?.timestamp_iso;
  return (
    <div className="evidence">
      <p className="quote">&ldquo;{evidence?.quote}&rdquo;</p>
      {(iso || evidence?.timestamp) && (
        <button
          type="button"
          className="evidence-jump mono"
          disabled={!iso}
          onClick={() => iso && onNavigateToTimestamp?.(iso)}
          title={iso ? 'Jump to this moment in the transcript' : undefined}
        >
          ▶ {evidence.timestamp || iso}
        </button>
      )}
    </div>
  );
}

/**
 * The verdict: bullets when the analyzer produced them, the original paragraph
 * when it didn't. Callers guard on there being one or the other.
 */
function PointsOrProse({ points, prose }: { points?: string[]; prose?: string }) {
  const list = (points ?? []).filter(Boolean);
  if (list.length === 0) return <p>{prose}</p>;
  return (
    <ul className="point-list">
      {list.map((point, index) => (
        <li key={index}>{point}</li>
      ))}
    </ul>
  );
}

/** One "what went well" bullet with the moment that proves it. */
function StrengthItem({
  strength,
  onNavigateToTimestamp
}: {
  strength: RubricStrength;
  onNavigateToTimestamp?: (iso: string) => void;
}) {
  return (
    <li>
      <span>{strength?.point}</span>
      {strength?.evidence?.quote && (
        <div className="point-detail">
          <EvidenceItem
            evidence={strength.evidence}
            onNavigateToTimestamp={onNavigateToTimestamp}
          />
        </div>
      )}
    </li>
  );
}

/** One "the fix" bullet with the words the rep could have said instead. */
function FixItem({ fix }: { fix: RubricFix }) {
  return (
    <li>
      <span>{fix?.point}</span>
      {fix?.suggested_language && (
        <div className="point-detail">
          <div className="suggested">
            <div className="panel-label">Suggested language</div>
            <p className="quote">&ldquo;{fix.suggested_language}&rdquo;</p>
            {fix?.playbook_reference && (
              <div className="caption">Playbook: {fix.playbook_reference}</div>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/** Compact score-sheet card. Opens the detail modal. */
function CategoryCard({
  category,
  showWeight,
  onOpen
}: {
  category: RubricCategoryResult;
  showWeight: boolean;
  onOpen: () => void;
}) {
  const tone = scoreTone(category?.raw_score);
  const references = category?.evidence?.length ?? 0;
  const ungraded = category?.raw_score == null || !!category?.error;
  const weight = category?.weight;

  return (
    <button type="button" className={'rubric-card ' + tone} onClick={onOpen}>
      <span className="accent" aria-hidden />
      <div className="rubric-card-top">
        <span className="mono num">{pad2(category?.number)}</span>
        <span className="rubric-card-badges">
          {showWeight && weight != null && (
            <span
              className={'weight mono' + (weight > 1 ? ' up' : weight < 1 ? ' down' : '')}
              title={`Counts ${weight.toFixed(1)}× toward this call's score`}
            >
              {weight.toFixed(1)}×
            </span>
          )}
          <span className={'score-chip ' + tone}>
            {category?.raw_score != null ? `${category.raw_score}/5` : '—'}
          </span>
        </span>
      </div>
      <div className="rubric-card-title">{category?.title}</div>
      <div className="rubric-card-foot">
        {ungraded ? 'Not graded' : `${references} ref${references === 1 ? '' : 's'}`}
      </div>
    </button>
  );
}

/** Full detail for one dimension. */
function CategoryModal({
  category,
  onClose,
  onNavigateToTimestamp
}: {
  category: RubricCategoryResult;
  onClose: () => void;
  onNavigateToTimestamp?: (iso: string) => void;
}) {
  const tone = scoreTone(category?.raw_score);
  const evidence = category?.evidence ?? [];
  const strengths = category?.strengths ?? [];
  const fixes = category?.fixes ?? [];
  // This panel justifies the SCORE — including moments that show what went
  // wrong, which the strengths column cannot carry. It stays, but once the
  // strengths quote their own moments it opens on a near-duplicate list, so it
  // starts collapsed whenever those per-point quotes exist.
  const hasInlineEvidence = strengths.some((item) => !!item?.evidence?.quote);
  const [evidenceOpen, setEvidenceOpen] = useState(!hasInlineEvidence);

  // Escape closes; body scroll is locked while open.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  // Jumping to the transcript is pointless behind a modal.
  const jump = (iso: string) => {
    onNavigateToTimestamp?.(iso);
    onClose();
  };

  return (
    <div className="modal-scrim" onClick={onClose} role="dialog" aria-modal="true" aria-label={category?.title}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">
            <span className={'score-tile ' + tone}>
              <b>{category?.raw_score != null ? category.raw_score : '—'}</b>
              <span>/ 5</span>
            </span>
            <div>
              <div className="eyebrow" style={{ marginBottom: 4 }}>
                Dimension {pad2(category?.number)}
              </div>
              <h3>{category?.title}</h3>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          {category?.error || category?.raw_score == null ? (
            <EmptyState>
              This dimension could not be graded automatically for this call. Re-running the analysis
              is done from the client&rsquo;s own admin portal.
            </EmptyState>
          ) : (
            <>
              {(category?.summary_points?.length || category?.justification) && (
                <div className="panel">
                  <div className="panel-label">Verdict</div>
                  <PointsOrProse
                    points={category?.summary_points}
                    prose={category?.justification}
                  />
                </div>
              )}

              <div className="panel">
                <button
                  type="button"
                  className="panel-toggle"
                  onClick={() => setEvidenceOpen((open) => !open)}
                  aria-expanded={evidenceOpen}
                >
                  <span className="panel-label">Evidence ({evidence.length})</span>
                  <span className={'panel-chevron' + (evidenceOpen ? ' open' : '')} aria-hidden>
                    ▾
                  </span>
                </button>
                {evidenceOpen &&
                  (evidence.length > 0 ? (
                    <div className="evidence-list">
                      {evidence.map((item, index) => (
                        <EvidenceItem key={index} evidence={item} onNavigateToTimestamp={jump} />
                      ))}
                    </div>
                  ) : (
                    <p className="caption">No evidence captured for this category.</p>
                  ))}
              </div>

              {/* What went well (with its evidence) / The fix (with the words to
                  use instead). Each side falls back to its paragraph for calls
                  graded before bullets existed. */}
              <div className="split">
                <div className="panel pos">
                  <div className="panel-label">✓ What went well</div>
                  {strengths.length > 0 ? (
                    <ul className="point-list">
                      {strengths.map((strength, index) => (
                        <StrengthItem
                          key={index}
                          strength={strength}
                          onNavigateToTimestamp={jump}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p>{category?.what_went_well || '—'}</p>
                  )}
                </div>
                <div className="panel warn">
                  <div className="panel-label">✎ The fix</div>
                  {fixes.length > 0 ? (
                    <ul className="point-list">
                      {fixes.map((fix, index) => (
                        <FixItem key={index} fix={fix} />
                      ))}
                    </ul>
                  ) : (
                    <p>{category?.what_was_missed || '—'}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SessionRubric({
  rubricAnalysis,
  multipleSalespeople,
  onNavigateToTimestamp
}: Props) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  if (!rubricAnalysis) {
    return <EmptyState>No rubric analysis available for this call.</EmptyState>;
  }

  const categories = rubricAnalysis.categories ?? [];
  // A dimension "failed" with no score or an error. When any did, the overall
  // band was computed from only the graded ones, so it must not be presented
  // as final.
  const failed = categories.filter((category) => category?.raw_score == null || !!category?.error);
  const gradedCount = categories.length - failed.length;

  const tone = BAND_TONE[rubricAnalysis.band ?? ''] ?? '';
  const percentage =
    rubricAnalysis.percentage != null ? Math.round(rubricAnalysis.percentage * 100) : null;
  const equalWeightPercentage =
    rubricAnalysis.equal_weight_percentage != null
      ? Math.round(rubricAnalysis.equal_weight_percentage * 100)
      : null;
  const narrative = rubricAnalysis.narrative ?? {};
  const isCustomWeighted = !!rubricAnalysis.isCustomWeighted;
  const weighting = rubricAnalysis.weighting;

  const keyOf = (category: RubricCategoryResult, index: number) =>
    category?.key ?? String(category?.number ?? index);
  const selected = selectedKey
    ? categories.find((category, index) => keyOf(category, index) === selectedKey) ?? null
    : null;

  return (
    <div className="rubric">
      {failed.length > 0 && (
        <div className="panel-note neg">
          <b>
            Incomplete analysis — {failed.length} of {categories.length} dimension
            {failed.length === 1 ? '' : 's'} could not be graded.
          </b>{' '}
          The overall score below came from only the {gradedCount} graded dimension
          {gradedCount === 1 ? '' : 's'}, so it may not reflect the full call. Affected:{' '}
          {failed.map((category, index) => category?.title ?? category?.key ?? `#${index + 1}`).join(', ')}.
        </div>
      )}

      {/* Score summary */}
      <div className={'rubric-summary ' + tone}>
        <div>
          <div className="eyebrow">
            Discovery Call Rubric
            {multipleSalespeople && <span className="chip info">Team score · 2 salespeople</span>}
            {isCustomWeighted && (
              <span
                className="chip"
                title={
                  weighting?.adjustedByName
                    ? `Weights adjusted by ${weighting.adjustedByName}`
                    : 'Graded with manager-set weights rather than equal weighting'
                }
              >
                Custom weights
              </span>
            )}
          </div>
          <div className={'band ' + tone}>
            {rubricAnalysis.band_label ?? '—'}
            {failed.length > 0 && <span className="chip neg">Partial</span>}
          </div>
          {rubricAnalysis.band_interpretation && (
            <p className="desc">{rubricAnalysis.band_interpretation}</p>
          )}
          <p className="caption">
            {rubricAnalysis.scored_count ?? categories.length}/
            {rubricAnalysis.category_count ?? 10} categories graded
          </p>
        </div>
        <div className="rubric-total">
          <div className="val">
            {rubricAnalysis.weighted_total ?? '–'}
            <span className="max"> / {rubricAnalysis.weighted_max ?? '–'}</span>
          </div>
          {percentage != null && <div className="pct">{percentage}%</div>}
        </div>
      </div>

      {/* How the score was arrived at */}
      {(narrative.weighting_rationale || narrative.scoring_approach) && (
        <section className="section">
          <div className="section-head">
            <div>
              <h3>How this call was scored</h3>
            </div>
          </div>
          <div className="panel">
            {narrative.weighting_rationale && (
              <>
                {/* At equal weights there is no weighting decision to report —
                    the block names what moved the total instead. */}
                <div className="panel-label">
                  {isCustomWeighted ? 'Weighting rationale' : 'What drove the score'}
                </div>
                <p>{narrative.weighting_rationale}</p>
              </>
            )}
            {narrative.scoring_approach && (
              <>
                <div className="panel-label" style={{ marginTop: 14 }}>
                  Scoring approach
                </div>
                <p>{narrative.scoring_approach}</p>
              </>
            )}
            <p className="caption" style={{ marginTop: 14 }}>
              {isCustomWeighted ? (
                <>
                  <b>Custom weights</b>
                  {weighting?.adjustedByName ? ` · set by ${weighting.adjustedByName}` : ''}
                  {/* A reweighted score only means something next to the
                      unweighted one — otherwise cross-rep comparison drifts. */}
                  {equalWeightPercentage != null
                    ? ` · ${equalWeightPercentage}% at equal weights${
                        rubricAnalysis.equal_weight_band_label
                          ? ` (${rubricAnalysis.equal_weight_band_label})`
                          : ''
                      }`
                    : ''}
                </>
              ) : (
                <>
                  <b>Equal weights</b> · all ten dimensions count the same on this call
                </>
              )}
            </p>
          </div>
        </section>
      )}

      {/* Coach's read */}
      {(narrative.highest_leverage_strength ||
        narrative.highest_leverage_fix ||
        narrative.where_the_deal_stands) && (
        <section className="section">
          <div className="section-head">
            <div>
              <h3>Coach&rsquo;s read</h3>
            </div>
          </div>
          <div className="panel">
            {narrative.highest_leverage_strength && (
              <div className="read-block pos">
                <div className="panel-label">Highest-leverage strength</div>
                <p>{narrative.highest_leverage_strength}</p>
              </div>
            )}
            {narrative.highest_leverage_fix && (
              <div className="read-block warn">
                <div className="panel-label">Highest-leverage fix</div>
                <p>{narrative.highest_leverage_fix}</p>
              </div>
            )}
            {narrative.where_the_deal_stands && (
              <div className="read-block">
                <div className="panel-label">Where the deal stands</div>
                <p>{narrative.where_the_deal_stands}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Score sheet */}
      <section className="section">
        <div className="section-head">
          <div>
            <h3>Score sheet</h3>
            <p className="desc">Click a dimension for its verdict and evidence.</p>
          </div>
        </div>
        {categories.length === 0 ? (
          <EmptyState>No categories were scored.</EmptyState>
        ) : (
          <div className="rubric-grid">
            {categories.map((category, index) => {
              const key = keyOf(category, index);
              return (
                <CategoryCard
                  key={key}
                  category={category}
                  showWeight={isCustomWeighted}
                  onOpen={() => setSelectedKey(key)}
                />
              );
            })}
          </div>
        )}
      </section>

      {selected && (
        <CategoryModal
          category={selected}
          onClose={() => setSelectedKey(null)}
          onNavigateToTimestamp={onNavigateToTimestamp}
        />
      )}
    </div>
  );
}
